import { db } from '@app/icare/client/db'
import type { ResultTypeFromQueryResult } from '@app/icare/client/db/client/localQueryOptions'
import { localPaginatedQueryOptions } from '@app/icare/client/db/client/localQueryOptions'
import { queryKit } from '@app/icare/db/query/queryKit'
import { teamNameForGroup } from '@app/icare/entities/adTeam/schema/ADTeam'
import { entityRegistry } from '@app/icare/entities/registry'
import { UserRoleEnum } from '@app/icare/entities/user/schema/User'
import { Trans } from '@lingui/react/macro'
import { createQueryStore, QueryBaseSchema, useColumnDef } from '@pkg/api/query'
import { AddEntityButtonLink, ButtonLink, FilterButton } from '@pkg/ui/button'
import { LinkIndicatorIcon, SearchInput } from '@pkg/ui/common'
import { usePageContainerOverride } from '@pkg/ui/layout/PageContainerRuntime'
import { QueryFilterWrapper } from '@pkg/ui/query/QueryFilterWrapper'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Divider, Space, Table, Tag } from 'antd'
import React from 'react'
import { z } from 'zod'
import { useShallow } from 'zustand/react/shallow'

export const Route = createFileRoute('/_app/mitarbeiter/')({
  component: RouteComponent,
})

const Filter = QueryBaseSchema.extend({
  sortBy: z.enum(['lastName', 'firstName']).nullish(),
})

const filter = createQueryStore({
  schema: Filter,
})

const { label } = entityRegistry.user
function RouteComponent() {
  const [open, setOpen] = React.useState(false)
  const f = filter(useShallow(s => s))

  const params = queryKit.parseQuery(f, { paginationMode: 'offset' })

  const { data, isFetching } = useQuery(
    localPaginatedQueryOptions({
      query: db.selectFrom('user').selectAll().$call(queryKit.all(params)).$call(queryKit.deleted(f.deleted)),
      params,
    }),
  )

  const columns = useColumnDef<ResultTypeFromQueryResult<typeof data>>(
    () => [
      {
        key: 'user.lastName',
        title: <Trans>Nachname</Trans>,
        fixed: 'start',
        render: (_, row) => <div className="font-medium text-gray-900">{row.lastName}</div>,
      },
      {
        key: 'user.firstName',
        title: <Trans>Vorname</Trans>,
        fixed: 'start',
        render: (_, row) => <div className="font-medium text-gray-900">{row.firstName}</div>,
      },
      {
        key: 'user.roles',
        title: <Trans>Rollen</Trans>,
        render: (_, row) => {
          const roleTags = (row.roles ?? []).filter((r): r is typeof UserRoleEnum.$.Type =>
            UserRoleEnum.$.values.includes(r),
          )
          return roleTags.length > 0 ? (
            <Space size={[0, 4]} wrap>
              {roleTags.map(role => (
                <Tag key={role} color="blue">
                  {UserRoleEnum.$.getLabel(role)}
                </Tag>
              ))}
            </Space>
          ) : null
        },
      },
      {
        key: 'teams',
        title: <Trans>Teams</Trans>,
        sorter: false,
        render: (_, row) => {
          const teams = (row.roles ?? []).filter(g => g.startsWith('CARE_Team_'))
          return teams.length > 0 ? (
            <Space size={[0, 4]} wrap>
              {teams.map(group => (
                <Tag key={group} color="geekblue">
                  {teamNameForGroup(group)}
                </Tag>
              ))}
            </Space>
          ) : null
        },
      },
      {
        key: 'actions',
        title: <Trans>Aktionen</Trans>,
        sorter: false,
        fixed: 'end',
        render: (_, row) => (
          <ButtonLink to="/mitarbeiter/$id" params={{ id: row.id }} size="small" shape="round">
            <Trans>Anzeigen</Trans>
            <LinkIndicatorIcon />
          </ButtonLink>
        ),
      },
    ],
    [],
    filter,
  )

  usePageContainerOverride(
    {
      header: {
        extra: (
          <Space separator={<Divider vertical />} wrap={false}>
            <Space size="small">
              <SearchInput value={f.meta.searchRawValue} onChange={e => f.setSearch(e.target.value)} />
              <FilterButton activeFilterCount={f.meta.activeFilterCount} onClick={() => setOpen(true)} />
            </Space>
            <AddEntityButtonLink to="/mitarbeiter/$id" params={{ id: 'new' }} entityName={label} />
          </Space>
        ),
        footer: <Divider size="small" style={{ marginBottom: 0 }} />,
      },
    },
    [f.meta.searchRawValue, f.setSearch, f.meta.activeFilterCount, setOpen, f.meta.activeFilterCount],
  )
  return (
    <QueryFilterWrapper queryStore={filter} open={open} onOpenChange={setOpen}>
      <Table
        className="min-w-full"
        dataSource={data?.data ?? []}
        loading={isFetching}
        columns={columns}
        rowKey="id"
        pagination={data}
        onChange={f.onChange}
        size="small"
        scroll={{ x: 'max-content' }}
      />
    </QueryFilterWrapper>
  )
}
